import clsx from 'clsx'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { memo } from 'react'

import RadioButton from '@/atoms/RadioButton'
import usePriceRange from '@/hooks/usePriceRange'
import Accordion from '@/molecules/Accordion/Accordion'
import PriceRange from '@/molecules/PriceRange'
import useModalStore from '@/store/slices/modal'
import FilterCategories from '@/ts/enums/global/FilterCategories'
import { FilterOption } from '@/ts/interfaces/global/FilterGroup'

import BottomBar from './includes/BottomBar'
import Controls from './includes/Controls'
import List from './includes/List'
import { FilterBoxProps } from './ts'

function FilterBox({
  count,
  filters,
  maxPrice = 1,
  withoutSort,
}: FilterBoxProps) {
  const { t } = useTranslation('products')
  const { price_from, price_to } = usePriceRange()
  const router = useRouter()

  const groups = Object.entries(filters) as [FilterCategories, FilterOption[]][]

  const { name } = useModalStore()
  const shouldBeVisible = name === 'filter_box'

  const viableGroups = groups.filter(([_, options]) => options?.length > 1)

  const withFilters = !!viableGroups.length

  return (
    <>
      {(withFilters || maxPrice > 0) && (
        <aside
          className={clsx(
            'fixed inset-0 bg-white flex-shrink-0 w-full xl:z-auto xl:static xl:opacity-100 xl:visible xl:translate-y-0 xl:transition-none xl:max-w-[220px]',
            {
              'opacity-0 -translate-y-8 transition-all ease-linear duration-200 -z-2': !shouldBeVisible,
              'opacity-100 translate-y-0 visible z-above-all xl:z-auto transition-all duration-300 ease-out': shouldBeVisible,
            }
          )}
        >
          <div className="flex flex-col w-full h-full p-6 pb-40 overflow-y-auto xl:pt-0.5 scrollbar-hidden xl:overflow-visible xl:p-0">
            <Controls />
            {withFilters && (
              <div className="flex flex-col mb-10 space-y-10">
                {viableGroups.map(([category, options]) => (
                  <Accordion
                    key={category}
                    heading={t(category)}
                    alwaysOpen={groups.length <= 1}
                  >
                    <List category={category} options={options} />
                  </Accordion>
                ))}
              </div>
            )}
            {!router.pathname.includes('creators') &&
              (maxPrice > 0 ||
                price_from !== undefined ||
                price_to !== undefined) && (
                <Accordion heading="Cena">
                  <PriceRange
                    max={maxPrice / 100}
                    range={[price_from, price_to]}
                  />
                </Accordion>
              )}
            <div className="mt-12">
              {!withoutSort && (
                <Accordion heading="Sortowanie">
                  <div className="space-y-4">
                    <fieldset className="space-y-4">
                      <RadioButton
                        name="sort"
                        label="Popularność"
                        onChange={() =>
                          router.push({
                            query: {
                              ...router.query,
                              sort_by: 'orders_count',
                              direction: 'asc',
                            },
                          })
                        }
                        checked={router.query.sort_by === 'orders_count'}
                      />
                      <RadioButton
                        name="sort"
                        label="Najniższa cena"
                        onChange={() =>
                          router.push({
                            query: {
                              ...router.query,
                              sort_by: 'price',
                              direction: 'asc',
                            },
                          })
                        }
                        checked={
                          router.query.sort_by === 'price' &&
                          router.query.direction === 'asc'
                        }
                      />
                      <RadioButton
                        name="sort"
                        label="Najwyższa cena"
                        onChange={() =>
                          router.push({
                            query: {
                              ...router.query,
                              sort_by: 'price',
                              direction: 'desc',
                            },
                          })
                        }
                        checked={
                          router.query.sort_by === 'price' &&
                          router.query.direction === 'desc'
                        }
                      />
                      <RadioButton
                        name="sort"
                        label="Domyślne"
                        onChange={() =>
                          router.push({
                            query: {},
                          })
                        }
                        checked={router.query.sort_by === undefined}
                      />
                    </fieldset>
                  </div>
                </Accordion>
              )}
            </div>
          </div>
          <BottomBar count={count} />
        </aside>
      )}
    </>
  )
}

export default memo(FilterBox)
